import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { Icon } from './Icon';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'gold';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-on-primary hover:bg-primary-hover active:bg-primary-active',
  secondary: 'bg-surface text-primary border border-border hover:bg-surface-tint hover:border-border-strong',
  ghost: 'bg-transparent text-link hover:bg-surface-tint',
  danger: 'bg-danger text-on-danger hover:bg-danger-hover',
  // Gold is reserved for milestones: issue certificate, mark mastered, award Ijazah (design system §4.1).
  gold: 'bg-accent text-on-accent hover:brightness-95',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-control-sm px-3 text-sm gap-1.5',
  md: 'h-control-md px-4 text-sm gap-2 max-md:min-h-touch-min',
  lg: 'h-control-lg px-6 text-md gap-2',
};

export const buttonClasses = (variant: ButtonVariant = 'primary', size: ButtonSize = 'md', extra?: string) =>
  cn(
    'inline-flex select-none items-center justify-center whitespace-normal rounded-md text-center font-medium',
    'transition-colors duration-micro ease-out motion-safe:active:scale-[0.98]',
    'disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
    VARIANTS[variant],
    SIZES[size],
    extra,
  );

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leadingIcon?: LucideIcon;
  trailingIcon?: LucideIcon;
  fullWidth?: boolean;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, leadingIcon, trailingIcon, fullWidth, className, children, disabled, type, ...rest },
  ref,
) {
  const iconSize = size === 'sm' ? 16 : 20;
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={buttonClasses(variant, size, cn(fullWidth && 'w-full', className))}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={iconSize} /> : leadingIcon ? <Icon icon={leadingIcon} size={iconSize} /> : null}
      {children}
      {trailingIcon && !loading ? <Icon icon={trailingIcon} size={iconSize} /> : null}
    </button>
  );
});
